import { Scan, Pattern } from '../types/scan'
import { WeeklyInsight, InsightSummary } from '../types/insight'

/**
 * Service to detect meaningful changes and generate insights from scan history
 */

// Helper to get start of week (Monday)
function getStartOfWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d
}

// Helper to calculate percentage change
function calculateChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

export const InsightDetectionService = {
  /**
   * Analyze scans to detect weekly insights
   */
  detectWeeklyInsights(scans: Scan[]): InsightSummary {
    const now = new Date()
    const currentWeekStart = getStartOfWeek(now)
    const previousWeekStart = new Date(currentWeekStart)
    previousWeekStart.setDate(previousWeekStart.getDate() - 7)

    // Filter scans by week
    const currentWeekScans = scans.filter((s) => {
      const d = new Date(s.created_at)
      return d >= currentWeekStart && s.status === 'completed'
    })

    const previousWeekScans = scans.filter((s) => {
      const d = new Date(s.created_at)
      return (
        d >= previousWeekStart &&
        d < currentWeekStart &&
        s.status === 'completed'
      )
    })

    const insights: WeeklyInsight[] = []

    // 1. Check for Activity Milestones
    if (currentWeekScans.length > 0 && previousWeekScans.length === 0) {
      insights.push({
        id: 'first-activity',
        type: 'milestone',
        title: 'Weekly Activity Started',
        description:
          "You've started analyzing your conversations this week. Keep it up to see trends!",
        priority: 'medium',
        date: now.toISOString(),
        actionLink: '/all-scans',
        actionText: 'View Scans',
      })
    } else if (currentWeekScans.length >= 5) {
      insights.push({
        id: 'high-activity',
        type: 'milestone',
        title: 'High Engagement Week',
        description: `You've analyzed ${currentWeekScans.length} conversations this week. That's great dedication to improvement!`,
        priority: 'low',
        date: now.toISOString(),
        metric: {
          label: 'Scans',
          value: currentWeekScans.length,
          change: currentWeekScans.length - previousWeekScans.length,
          direction: 'up',
          unit: 'scans',
        },
      })
    }

    // 2. Analyze Toxicity Score Trends
    if (currentWeekScans.length > 0 && previousWeekScans.length > 0) {
      const currentAvg =
        currentWeekScans.reduce((sum, s) => sum + (s.score || 0), 0) /
        currentWeekScans.length
      const prevAvg =
        previousWeekScans.reduce((sum, s) => sum + (s.score || 0), 0) /
        previousWeekScans.length
      const change = calculateChange(currentAvg, prevAvg)

      // Significant improvement (score dropped by > 15%)
      // Note: Lower score is better for toxicity
      if (change <= -15) {
        insights.push({
          id: 'toxicity-improvement',
          type: 'improvement',
          title: 'Communication Improving',
          description:
            'Your average toxicity score has dropped significantly compared to last week.',
          priority: 'high',
          date: now.toISOString(),
          metric: {
            label: 'Avg Score',
            value: Math.round(currentAvg),
            change: Math.abs(Math.round(change)),
            direction: 'down', // down is good for toxicity
            unit: '%',
          },
          actionLink: '/analysis',
          actionText: 'See Details',
        })
      }
      // Significant regression (score increased by > 15%)
      else if (change >= 15) {
        insights.push({
          id: 'toxicity-regression',
          type: 'regression',
          title: 'Tension Detected',
          description:
            'We noticed a spike in toxicity scores this week. Consider reviewing recent stressful conversations.',
          priority: 'high',
          date: now.toISOString(),
          metric: {
            label: 'Avg Score',
            value: Math.round(currentAvg),
            change: Math.round(change),
            direction: 'up', // up is bad for toxicity
            unit: '%',
          },
          actionLink: '/blog',
          actionText: 'Get Tips',
        })
      }
    }

    // 3. Analyze Pattern Shifts
    const getPatternCounts = (scanList: Scan[]) => {
      const counts: Record<string, number> = {}
      scanList.forEach((s) => {
        // Defensive check: ensure patterns exists and is an array
        if (s.patterns && Array.isArray(s.patterns)) {
          s.patterns.forEach((p) => {
            counts[p.type] = (counts[p.type] || 0) + 1
          })
        }
      })
      return counts
    }

    const currentPatterns = getPatternCounts(currentWeekScans)
    const prevPatterns = getPatternCounts(previousWeekScans)

    // Check for new emerging patterns
    Object.entries(currentPatterns).forEach(([pattern, count]) => {
      // If pattern appears in > 30% of scans and wasn't prominent before
      const frequency = count / currentWeekScans.length
      const prevCount = prevPatterns[pattern] || 0
      const prevFrequency = previousWeekScans.length
        ? prevCount / previousWeekScans.length
        : 0

      if (frequency > 0.3 && frequency > prevFrequency * 2) {
        insights.push({
          id: `pattern-${pattern}`,
          type: 'pattern_emerged',
          title: 'New Pattern Emerging',
          description: `We're seeing "${pattern}" appear more frequently in your conversations this week.`,
          priority: 'medium',
          date: now.toISOString(),
          metric: {
            label: 'Frequency',
            value: Math.round(frequency * 100),
            change: Math.round((frequency - prevFrequency) * 100),
            direction: 'up',
            unit: '%',
          },
        })
      }
    })

    // Sort by priority
    const priorityWeight = { high: 3, medium: 2, low: 1 }
    insights.sort(
      (a, b) => priorityWeight[b.priority] - priorityWeight[a.priority],
    )

    return {
      hasMeaningfulChange: insights.some(
        (i) => i.priority === 'high' || i.type === 'pattern_emerged',
      ),
      primaryInsight: insights.length > 0 ? insights[0] : null,
      allInsights: insights,
      weekStart: currentWeekStart.toISOString(),
      weekEnd: new Date(
        currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000,
      ).toISOString(),
    }
  },

  /**
   * Determine if an email should be triggered based on insights
   */
  shouldTriggerEmail(summary: InsightSummary, lastEmailDate?: string): boolean {
    if (!summary.hasMeaningfulChange) return false

    // Don't spam - check if email was sent recently (e.g., within 3 days)
    if (lastEmailDate) {
      const lastSent = new Date(lastEmailDate)
      const now = new Date()
      const diffHours = (now.getTime() - lastSent.getTime()) / (1000 * 60 * 60)
      if (diffHours < 72) return false
    }

    return true
  },
}
